import { optionsPerPage } from '@/constants';
import { PageSize, SortDirection } from '@/constants/enum';
import { ShipmentStatus } from '@/constants/shipment';
import { camelToRegularText, camelToSnakeCase } from '@/helpers';
import { useDebounce } from '@/hooks';
import { useGetRawCourierListQuery } from '@/redux/api/api.caller';
import shipmentSlice, { shipmentFilterSelector } from '@/redux/features/shipment.slice';
import { IResponse } from '@/types';
import { IShipmentSearch, IStatusSummary } from '@/types/shipment';
import {
  Autocomplete,
  ChoiceList,
  Icon,
  IndexFilters,
  IndexFiltersProps,
  TabProps,
  useBreakpoints,
  useSetIndexFiltersMode,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EShipmentType } from '../../../../config';
import { sortOptions } from './config';

interface IShipmentFilter {
  data: IResponse<IShipmentSearch> | undefined;
  isLoading: boolean;
  type: EShipmentType;
  courierRange?: { from?: string; to?: string };
}

const AutocompleteTextField = ({
  courierInputValue,
  setCourierInputValue,
}: {
  courierInputValue: string;
  setCourierInputValue: (value: string) => void;
}) => {
  useEffect(() => {
    return () => setCourierInputValue('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Autocomplete.TextField
      label="Courier"
      labelHidden
      value={courierInputValue}
      onChange={setCourierInputValue}
      placeholder="Search couriers"
      autoComplete="off"
      prefix={<Icon source={SearchIcon} />}
    />
  );
};

const ShipmentFilter = ({ data: shipmentData, isLoading: isShipmentLoading, courierRange }: IShipmentFilter) => {
  const { xsDown: isNarrowScreen } = useBreakpoints();
  const { mode, setMode } = useSetIndexFiltersMode();
  const dispatch = useDispatch();
  const shipmentFilter = useSelector(shipmentFilterSelector);
  const [courierInputValue, setCourierInputValue] = useState('');
  const debouncedCourierSearch = useDebounce<string>(courierInputValue);
  const { data: courierData, isLoading: isCourierLoading } = useGetRawCourierListQuery(
    {
      page: 1,
      perPage: 500,
      from: courierRange?.from,
      to: courierRange?.to,
      search: debouncedCourierSearch.trim() || undefined,
    },
    { skip: mode === 'DEFAULT' },
  );

  const handleUpdateSort = (value: string[]) => {
    const formattedValue = value.toString().split(' ');
    dispatch(
      shipmentSlice.actions.handleChangeTableFilter({
        ...shipmentFilter,
        sortBy: formattedValue[0],
        sortOrder: formattedValue[1].toUpperCase() as SortDirection,
      }),
    );
  };

  const courierOptions = useMemo(() => {
    return (courierData?.data?.rawCouriers ?? []).map(({ rawCourier }) => ({ value: rawCourier, label: rawCourier }));
  }, [courierData]);

  const statusOptions = useMemo(() => {
    return [
      { value: ShipmentStatus.Pending, label: 'Pending' },
      { value: ShipmentStatus.InformationReceived, label: 'Information Received' },
      { value: ShipmentStatus.InTransit, label: 'In Transit' },
      { value: ShipmentStatus.AvailableForPickup, label: 'Available For Pickup' },
      { value: ShipmentStatus.OutForDelivery, label: 'Out For Delivery' },
      { value: ShipmentStatus.Delivered, label: 'Delivered' },
      { value: ShipmentStatus.Exception, label: 'Exception' },
      { value: ShipmentStatus.FailedAttempt, label: 'Failed Attempt' },
      { value: ShipmentStatus.Expired, label: 'Expired' },
      { value: ShipmentStatus.CourierNotSupported, label: 'Courier Not Supported' },
      { value: ShipmentStatus.NotFound, label: 'No Info' },
      { value: ShipmentStatus.OverQuota, label: 'Over Quota' },
      { value: ShipmentStatus.CourierNotDetected, label: 'Courier Not Detected' },
    ];
  }, []);

  const filteredCourierOptions = useMemo(() => {
    if (!courierInputValue) return courierOptions;
    return courierOptions.filter((option) => option.label.toLowerCase().includes(courierInputValue.toLowerCase()));
  }, [courierOptions, courierInputValue]);

  const shipmentTabs: TabProps[] = useMemo(() => {
    if (shipmentFilter.type === EShipmentType.UNTRACKED_SHIPMENT || shipmentFilter.type === EShipmentType.UNFULFILLED_SHIPMENT) return [];
    // mảng key theo thứ tự mong muốn
    const statusSummaryByOrder: (keyof IStatusSummary)[] = [
      'all',
      'courierNotSupported',
      'expired',
      'exception',
      'failedAttempt',
      'informationReceived',
      'inTransit',
      'availableForPickup',
      'outForDelivery',
      'delivered',
      'pending',
      'courierNotDetected',
      'notFound',
      'overQuota',
    ];
    if (shipmentData) {
      let filteredStatuses = statusSummaryByOrder;

      if (shipmentFilter.statusIn.length > 0) {
        filteredStatuses = statusSummaryByOrder.filter((key) => {
          if (key === 'all') return true;
          const statusId = camelToSnakeCase(key);
          return shipmentFilter.statusIn.includes(statusId);
        });
      }

      return filteredStatuses.map((key) => {
        const value = shipmentData.data.statusSummary[key] ?? 0;

        return {
          id: camelToSnakeCase(key),
          content: `${key === 'notFound' ? 'No info' : camelToRegularText(key)} (${value})`,
        };
      });
    }
    return [];
  }, [shipmentData, shipmentFilter.type, shipmentFilter.statusIn]);

  const handleChangeTable = (key: string) => (value: string[]) => {
    dispatch(
      shipmentSlice.actions.handleChangeTableFilter({
        ...shipmentFilter,
        page: 1,
        [key]: value[0],
      }),
    );
  };

  const handleChangeCourier = useCallback(
    (selected: string[]) => {
      dispatch(
        shipmentSlice.actions.handleChangeTableFilter({
          ...shipmentFilter,
          page: 1,
          couriers: selected,
        }),
      );
    },
    [dispatch, shipmentFilter],
  );

  const handleChangeStatuses = useCallback(
    (selected: string[]) => {
      dispatch(
        shipmentSlice.actions.handleChangeTableFilter({
          ...shipmentFilter,
          status: ShipmentStatus.All,
          page: 1,
          statusIn: selected,
        }),
      );
    },
    [dispatch, shipmentFilter],
  );

  const handleClearAll = () => {
    dispatch(
      shipmentSlice.actions.handleChangeTableFilter({
        ...shipmentFilter,
        statusIndex: 0,
        status: ShipmentStatus.All,
        search: '',
        couriers: [],
        statusIn: [],
        page: 1,
        pageSize: PageSize.TwentyFive,
        sortOrder: SortDirection.DESC,
        sortBy: 'createdAt',
      }),
    );
  };

  const handleSelectTabs = (selectedTabIndex: number) =>
    dispatch(
      shipmentSlice.actions.handleChangeTableFilter({
        ...shipmentFilter,
        page: 1,
        statusIndex: selectedTabIndex,
        status: shipmentTabs[selectedTabIndex]?.id as ShipmentStatus,
      }),
    );

  const handleChangeInputSearch = (value: string) =>
    dispatch(shipmentSlice.actions.handleChangeTableFilter({ ...shipmentFilter, page: 1, search: value }));

  const appliedFilters: IndexFiltersProps['appliedFilters'] = [
    {
      onRemove: () => handleChangeTable('pageSize')([PageSize.TwentyFive.toString()]),
      key: 'pageSize',
      label: `Per page: ${shipmentFilter.pageSize}`,
    },
  ];

  if (shipmentFilter.couriers.length > 0) {
    appliedFilters.push({
      onRemove: () => handleChangeCourier([]),
      key: 'courier',
      label: `Courier: ${shipmentFilter.couriers.length > 0 ? `${shipmentFilter.couriers.length} selected` : 'All'}`,
    });
  }

  if (shipmentFilter.statusIn.length > 0) {
    appliedFilters.push({
      onRemove: () => handleChangeStatuses([]),
      key: 'statusIn',
      label: `Status: ${shipmentFilter.statusIn.length > 0 ? `${shipmentFilter.statusIn.length} selected` : 'All'}`,
    });
  }

  const filters = [
    {
      key: 'pageSize',
      label: 'Per page',
      filter: (
        <ChoiceList
          title="Per page"
          titleHidden
          choices={optionsPerPage}
          selected={[String(shipmentFilter.pageSize)]}
          onChange={handleChangeTable('pageSize')}
        />
      ),
      shortcut: true,
    },
  ];

  if (shipmentFilter.type === EShipmentType.TRACKED_SHIPMENT) {
    filters.push({
      key: 'courier',
      label: 'Courier',
      filter: (
        <Autocomplete
          allowMultiple
          options={filteredCourierOptions}
          selected={shipmentFilter.couriers}
          onSelect={handleChangeCourier}
          textField={<AutocompleteTextField courierInputValue={courierInputValue} setCourierInputValue={setCourierInputValue} />}
        />
      ),
      shortcut: true,
    });
    filters.push({
      key: 'statusIn',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          allowMultiple
          choices={statusOptions}
          selected={shipmentFilter.statusIn}
          onChange={handleChangeStatuses}
        />
      ),
      shortcut: true,
    });
  }

  const getQueryPlaceholder = () => {
    if (shipmentFilter.type === EShipmentType.UNFULFILLED_SHIPMENT) {
      return isNarrowScreen ? 'Order or email' : 'Search by order number or customer email';
    }
    return isNarrowScreen ? 'Tracking, order or email' : 'Search by tracking number, order number or customer email';
  };

  return (
    <IndexFilters
      mode={mode}
      setMode={setMode}
      cancelAction={{ onAction: () => {} }}
      canCreateNewView={false}
      sortOptions={sortOptions}
      sortSelected={[`${shipmentFilter.sortBy} ${shipmentFilter.sortOrder.toLowerCase()}`]}
      onSort={handleUpdateSort}
      queryPlaceholder={getQueryPlaceholder()}
      onQueryChange={handleChangeInputSearch}
      onQueryClear={() => handleChangeInputSearch('')}
      appliedFilters={appliedFilters}
      queryValue={shipmentFilter.search}
      filters={filters}
      onClearAll={handleClearAll}
      tabs={shipmentTabs}
      loading={isShipmentLoading || isCourierLoading}
      selected={shipmentFilter.statusIndex}
      onSelect={handleSelectTabs}
    />
  );
};

export default memo(ShipmentFilter);
